import { useEffect, useRef, type ReactNode } from 'react';
import { STATUS_PALETTE, type StatusTone } from './statusPalette';

/**
 * Вариантите на вграденото съобщение.
 *
 * 🔴 ВЪТРЕШЕН тип — НЕ се изнася, и това е гардът, не учтивост. `'success'` се
 * подава единствено от `InlineStatus` отдолу; прякото
 * `<InlineError variant="success">` **не се компилира**.
 *
 * Защо по конструкция, а не с коментар „ползвайте обвивката": правило, което
 * трябва да се ПОМНИ, е капан, който чака следващия — репото плати точно това на
 * 16.09, когато писано правило за плейсхолдъра беше нарушено от човек, участвал в
 * разговора за самото правило. Може ли инвариантът да стане проверим, става
 * проверим.
 */
type MessageVariant = 'error' | 'warning' | 'success';

/**
 * Кой тон от общата палитра носи всеки вариант. Цветовете живеят в `statusPalette.ts`,
 * не тук — `StatusPanel` чете същия източник, тоест „червеното за грешка" е едно.
 * `error` се казва `danger` в палитрата, защото там тонът не е само за грешки.
 */
const TONE: Record<MessageVariant, StatusTone> = {
  error: 'danger',
  warning: 'warning',
  success: 'success',
};

/**
 * 🔴 Ролята следва ВАРИАНТА, а не е закована.
 *
 * `role="alert"` е assertive live region: екранният четец ПРЕКЪСВА текущото
 * изречение. За грешка това е правилно. За „декларацията е подписана успешно" е
 * дефект — успехът се обявява като тревога, и то на екрана, който правим
 * достъпен на езика на родителя.
 *
 * ⚠️ `error` и `warning` запазват ДНЕШНАТА двойка (`role="alert"` + изричното
 * `aria-live="polite"`, което ѝ противоречи) — 28-те викача на подразбирането не
 * бива да усетят промяна. Противоречието е заварено: назовано, не поправено
 * мимоходом.
 */
const ROLES: Record<MessageVariant, 'alert' | 'status'> = {
  error: 'alert',
  warning: 'alert',
  success: 'status',
};

interface InlineMessageProps {
  message?: string | null | false;
  scrollIntoView?: boolean;
  onDismiss?: () => void;
  action?: ReactNode;
  className?: string;
  dismissLabel?: string;
  variant: MessageVariant;
}

function InlineMessage({
  message,
  scrollIntoView = false,
  onDismiss,
  action,
  className = '',
  dismissLabel = 'Dismiss',
  variant,
}: InlineMessageProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message && scrollIntoView && ref.current) {
      // 🔴 Извикването е НЕЗАДЪЛЖИТЕЛНО нарочно. `scrollIntoView` липсва в jsdom,
      // а грешка в `useEffect` без error boundary кара React 18 да ДЕМОНТИРА
      // цялото дърво — тоест липсваща спомагателна функция сваля целия екран.
      // Измерено 17.09: страницата изчезваше и тестът докладваше „не намирам
      // съобщението", което сочи към грешното място.
      ref.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [message, scrollIntoView]);

  if (!message) return null;

  return (
    <div
      ref={ref}
      role={ROLES[variant]}
      aria-live="polite"
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${STATUS_PALETTE[TONE[variant]].surface} ${className}`}
    >
      <span className="flex-1 break-words">{message}</span>
      {action && <span className="shrink-0">{action}</span>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-lg leading-none opacity-70 hover:opacity-100"
          aria-label={dismissLabel}
        >
          ×
        </button>
      )}
    </div>
  );
}

export interface InlineErrorProps {
  /** Съобщението. Falsy стойност не рендира нищо. */
  message?: string | null | false;
  /** Скролва съобщението във видимото при поява (когато стои под сгъвката). */
  scrollIntoView?: boolean;
  /** Незадължително затваряне. Подадено — рендира бутон ×. */
  onDismiss?: () => void;
  /**
   * Вид: червено по подразбиране, `'warning'` за жълто.
   * ⛔ Няма `'success'` — за успех има `InlineStatus`.
   */
  variant?: 'error' | 'warning';
  /** Незадължително действие вдясно (напр. „Опитай пак"). */
  action?: ReactNode;
  className?: string;
  /**
   * Достъпно име на бутона за затваряне. Подразбирането е английско и е било
   * единствената стойност досега; екраните, които се превеждат, подават свое.
   */
  dismissLabel?: string;
}

export default function InlineError(props: InlineErrorProps) {
  return <InlineMessage {...props} variant={props.variant ?? 'error'} />;
}

/**
 * Съобщение за УСПЕХ — единственият път до `variant='success'`.
 *
 * Отделно име, защото `InlineError` с проп `message`, рендиращ успех, е
 * семантичен смут на мястото на викане. Преименуване на целия компонент би
 * пипнало 29 файла извън обхвата на задачата, която го наложи — дупка, разширена
 * от самия изпълнител.
 */
export function InlineStatus(props: Omit<InlineErrorProps, 'variant'>) {
  return <InlineMessage {...props} variant="success" />;
}
